import React, { useCallback } from 'react'

import { UpdateSystemMessage, UpdateSystemMessageInput } from 'types/graphql'

import {
  FieldError,
  Form,
  Label,
  SubmitHandler,
  TextField,
  useForm,
} from '@cedarjs/forms'

import Button from 'src/components/Custom/Button/Button'
import { ResourceLinkType } from 'src/pages/SystemMessagingPage/ResourceLinksSection'

import HeadingLevel from '../Heading/Heading'

import CustomModal from './CustomModal'

export type ResourceLinkFormType = NonNullable<
  UpdateSystemMessage['updateSystemMessage']
>

interface ConfigureResourceLinkModalProps {
  header: string
  linkData?: ResourceLinkType
  showModal: boolean
  handleModalClose: () => void
  onSave: (
    data: UpdateSystemMessageInput,
    id?: ResourceLinkFormType['id']
  ) => void
}

type FormValues = {
  title?: string
  url?: string
}

const ConfigureResourceLinkModal = ({
  header,
  linkData,
  showModal,
  handleModalClose,
  onSave,
}: ConfigureResourceLinkModalProps) => {
  const form = useForm<FormValues>()

  const handleSave: SubmitHandler<FormValues> = async (data) => {
    const { title, url } = data
    // Skip save if no changes made.
    if (!form.formState.isDirty) {
      handleModalClose()
      return
    }
    try {
      const input = {
        title,
        url,
      }

      onSave(input, linkData?.id)
    } catch (error) {
      console.error('Save failed:', error)
    }
  }

  const closeModal = useCallback(() => {
    if (form.formState.isDirty) {
      if (
        window.confirm(
          'Are you sure you want to leave? You may lose unsaved changes.'
        )
      ) {
        handleModalClose()
      }
    } else {
      handleModalClose()
    }
  }, [form.formState.isDirty, handleModalClose])

  return (
    <CustomModal
      showModal={showModal}
      setShowModal={closeModal}
      modalWidth="max-w-xl relative"
    >
      <Form onSubmit={handleSave} formMethods={form}>
        <HeadingLevel level={1}>{header}</HeadingLevel>
        <Label
          name="title"
          className="rw-label mb-2 block w-full font-medium text-gray-700"
          errorClassName="rw-label rw-label-error"
        >
          Title
        </Label>
        <TextField
          name="title"
          defaultValue={linkData?.title}
          errorClassName="rw-input rw-input-error"
          className="focus:shadow-outline w-full appearance-none rounded border border-gray-300 px-0 pl-2 leading-tight text-gray-900 focus:outline-none"
        />

        <Label
          name="url"
          className="rw-label mb-2 block w-full font-medium text-gray-700"
          errorClassName="rw-label rw-label-error"
        >
          URL
        </Label>
        <TextField
          name="url"
          defaultValue={linkData?.url}
          errorClassName="rw-input rw-input-error"
          className="focus:shadow-outline w-full appearance-none rounded border border-gray-300 px-0 pl-2 leading-tight text-gray-900 focus:outline-none"
          validation={{
            required: 'Website URL is required',
            pattern: {
              value:
                /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
              message: 'Invalid URL format',
            },
          }}
        />
        <FieldError name="url" className="text-red-500" />

        <div className="mt-6 flex w-full justify-end gap-4">
          <Button
            title="Cancel"
            customButtonClass={'bg-white hover:bg-transparent'}
            onClick={closeModal}
          />
          <Button
            title="Save"
            type="submit"
            backgroundColor={'bg-indigo-600 hover:bg-indigo-800'}
          />
        </div>
      </Form>
    </CustomModal>
  )
}

export default ConfigureResourceLinkModal
